import { useId } from "react";

import Link from "next/link";
import { stegaClean } from "next-sanity";

import cn from "classnames";

import { isFrontendIconName } from "@/components/icons/iconRegistry";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";
import { urlFor } from "@/sanity/utils";

import type {
  StepsSectionBlockStep,
  StepsSectionData,
  StepsSectionImage,
  StepsSectionMediaKind,
  StepsSectionStepProps,
} from "./stepsSection.types";
import { StepsSectionList } from "./stepsSectionList.component";

const IMAGE_WIDTH = 800;

export function StepsSection({ block }: { block: StepsSectionData }) {
  const headingId = useId();
  const indicator = stegaClean(block.indicator) ?? "numberCircle";
  const mobileLayout = stegaClean(block.mobileLayout) ?? "carousel";
  const showIndicator = indicator === "numberCircle";
  const steps = (block.steps ?? []).map(toStepProps);

  if (steps.length === 0) return null;

  return (
    <section
      aria-labelledby={headingId}
      data-testid="steps-section"
      data-media={mediaKind(steps)}
      className="container flex w-full flex-col items-center gap-8 px-4 py-14 lg:gap-12">
      <div className="flex flex-col gap-4 text-center">
        <Text
          id={headingId}
          variant="h3"
          component="h2"
          className="text-secondary">
          {block.title}
        </Text>
        {block.subtitle ? (
          <Text variant="body1" component="p" className="text-greyscale-body">
            {block.subtitle}
          </Text>
        ) : null}
      </div>

      <StepsSectionList
        steps={steps}
        showIndicator={showIndicator}
        showConnector={showIndicator && block.showConnector !== false}
        mobileLayout={mobileLayout}
      />

      {block.ctaLabel && block.ctaLink?.url ? (
        <Link
          href={block.ctaLink.url}
          className={cn(
            brown.className,
            "border-secondary text-secondary hover:bg-secondary focus-visible:ring-secondary inline-flex min-h-12 items-center justify-center rounded-full border-2 px-8 py-3 text-sm font-bold uppercase transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          )}>
          {block.ctaLabel}
        </Link>
      ) : null}
    </section>
  );
}

function toStepProps(step: StepsSectionBlockStep): StepsSectionStepProps {
  const description = (
    <div className="text-greyscale-body [&_a]:text-secondary-100 [&_a]:font-normal">
      <RichText>{step.description}</RichText>
    </div>
  );
  const image = toImage(step.image);
  const icon = stegaClean(step.icon);

  if (image) {
    const mobile = toImage(step.mobileImage);
    return {
      key: step._key,
      title: step.title,
      description,
      media: {
        kind: "image",
        ...image,
        mobile: mobile ?? undefined,
      },
    };
  }

  return {
    key: step._key,
    title: step.title,
    description,
    media:
      icon && isFrontendIconName(icon) ? { kind: "icon", icon } : undefined,
  };
}

function toImage(image: StepsSectionImage | null) {
  if (!image?.asset) return null;
  const dimensions = image.asset.metadata?.dimensions;
  const width = dimensions?.width ?? IMAGE_WIDTH;
  const height = dimensions?.height ?? IMAGE_WIDTH;
  return {
    src: urlFor(image).width(IMAGE_WIDTH).fit("max").url(),
    width: IMAGE_WIDTH,
    height: Math.round((IMAGE_WIDTH * height) / width),
    alt: image.alt ?? "",
  };
}

function mediaKind(steps: StepsSectionStepProps[]): StepsSectionMediaKind {
  return steps[0]?.media?.kind ?? "none";
}
